// SS PWA — components/RoleGate.tsx — 2026-06-03
//
// Role gate for the new operator + owner diagnostics routes.
//
// Locked roles:
//   - 'ss_operator' — only Ernie. Sees /admin/diagnostics across all tenants.
//   - 'owner'       — per-tenant. Sees /:slug/health for own slug only.
//
// Source of truth: localStorage `ss-role` (set by the Worker session probe
// OR set manually by the operator for now until Cloudflare Access Groups
// wire role into the JWT). Failure-open is NOT acceptable here — gate
// defaults closed for the operator route, defaults open for the owner
// route since the owner is already past CF Access.

import type { ReactNode } from 'react';

type Role = 'ss_operator' | 'owner' | 'staff' | 'viewer' | '';

const ROLE_KEY = 'ss-role';

export function readRole(): Role {
  if (typeof localStorage === 'undefined') return '';
  return (localStorage.getItem(ROLE_KEY) as Role) ?? '';
}

export function setRole(role: Role): void {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(ROLE_KEY, role);
  }
}

interface RoleGateProps {
  required: Role | Role[];
  children: ReactNode;
  /** When set, render this instead of the default access-denied panel. */
  fallback?: ReactNode;
}

function asArray(r: Role | Role[]): Role[] {
  return Array.isArray(r) ? r : [r];
}

export function RoleGate({ required, children, fallback }: RoleGateProps) {
  const role = readRole();
  const allowed = asArray(required).includes(role);
  if (allowed) return <>{children}</>;
  if (fallback) return <>{fallback}</>;
  return (
    <div className="mx-auto max-w-md p-6">
      <div className="rounded-2xl border border-ink-200 bg-white p-6 text-center dark:border-[#1F2A37] dark:bg-[#0F172A]">
        <div className="mb-2 text-base font-semibold text-ink-900 dark:text-white">
          You don't have access to this page
        </div>
        <p className="text-sm text-ink-600 dark:text-[#94A3B8]">
          If you think this is wrong, ask Service Steady to grant you access.
        </p>
      </div>
    </div>
  );
}
